import { useEffect, useRef, useCallback } from "react";

interface UseAutoSliderProps {
  interval?: number;
  enabled?: boolean;
  onTick?: () => void;
}

export function useAutoSlider({
  interval = 5000,
  enabled = true,
  onTick,
}: UseAutoSliderProps = {}) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoplay = useCallback(() => {
    if (!enabled) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (onTick) onTick();
    }, interval);
  }, [interval, enabled, onTick]);

  const stopAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetAutoplay = useCallback(() => {
    stopAutoplay();
    startAutoplay();
  }, [stopAutoplay, startAutoplay]);

  useEffect(() => {
    if (enabled) startAutoplay();
    return () => stopAutoplay();
  }, [enabled, startAutoplay, stopAutoplay]);

  return {
    startAutoplay,
    stopAutoplay,
    resetAutoplay,
    isPlaying: !!intervalRef.current,
  };
}

export function useDomSlider() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{
    x: number;
    scrollLeft: number;
    time: number;
    lastX: number;
    lastTime: number;
    velocity: number;
  } | null>(null);

  const isDraggingRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);

  const stopMomentum = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  const handleMouseDown = useCallback((e: any) => {
    if (!containerRef.current) return;
    if (e.button !== 0) return; // Only drag with left click

    stopMomentum();

    const now = Date.now();
    dragStartRef.current = {
      x: e.clientX,
      scrollLeft: containerRef.current.scrollLeft,
      time: now,
      lastX: e.clientX,
      lastTime: now,
      velocity: 0,
    };
    isDraggingRef.current = false;
    document.body.style.userSelect = "none";
  }, [stopMomentum]);

  const handleMouseMove = useCallback((e: any) => {
    if (!dragStartRef.current || !containerRef.current) return;

    const now = Date.now();
    const dx = e.clientX - dragStartRef.current.x;

    if (Math.abs(dx) > 6) {
      isDraggingRef.current = true;
    }

    if (isDraggingRef.current) {
      const dt = now - dragStartRef.current.lastTime;
      if (dt > 0) {
        const stepDx = e.clientX - dragStartRef.current.lastX;
        dragStartRef.current.velocity = stepDx / dt; // px per ms
        dragStartRef.current.lastX = e.clientX;
        dragStartRef.current.lastTime = now;
      }
      containerRef.current.scrollLeft = dragStartRef.current.scrollLeft - dx;
    }
  }, []);

  const handleMouseUpOrLeave = useCallback(() => {
    if (!dragStartRef.current) return;

    const startInfo = dragStartRef.current;
    dragStartRef.current = null;
    document.body.style.userSelect = "";

    // Apply smooth momentum deceleration if released with velocity
    let velocity = startInfo.velocity * 16; // px per frame
    if (Math.abs(velocity) > 1 && containerRef.current) {
      const friction = 0.94;
      const step = () => {
        if (!containerRef.current || Math.abs(velocity) < 0.5) {
          stopMomentum();
          setTimeout(() => {
            isDraggingRef.current = false;
          }, 50);
          return;
        }
        containerRef.current.scrollLeft -= velocity;
        velocity *= friction;
        animFrameRef.current = requestAnimationFrame(step);
      };
      animFrameRef.current = requestAnimationFrame(step);
    } else {
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 50);
    }
  }, [stopMomentum]);

  const scrollPrev = useCallback((amount = 360) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -amount, behavior: "smooth" });
    }
  }, []);

  const scrollNext = useCallback((amount = 360) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  }, []);

  return {
    containerRef,
    isDraggingRef,
    scrollPrev,
    scrollNext,
    sliderEvents: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUpOrLeave,
      onMouseLeave: handleMouseUpOrLeave,
    },
  };
}
